import React from "react";
import { motion } from "framer-motion";
import { Wrench, Gauge, Sparkles as Sparkle, CarFront, CheckCheck } from "lucide-react";

const pillars = [
  {
    icon: CarFront,
    title: "Estética y carrocería",
    text: "Pintura, chapa, llantas, parabrisas, ópticas, interior y tapicería revisados con detalle.",
  },
  {
    icon: Wrench,
    title: "Mecánica y motor",
    text: "Motor, transmisión, embrague, fluidos, frenos, suspensión y dirección verificados a fondo.",
  },
  {
    icon: Gauge,
    title: "Sistemas y funcionalidad",
    text: "Electrónica, ADAS, climatización, multimedia, sensores y todos los sistemas en funcionamiento.",
  },
];

const stats = [
  { value: "200", label: "Puntos de revisión" },
  { value: "100%", label: "Inspección presencial" },
  { value: "0", label: "Sorpresas tras la entrega" },
];

export const Inspection = () => {
  return (
    <section
      id="inspeccion"
      className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/5 overflow-hidden"
      data-testid="inspection-section"
    >
      {/* Background image of mechanic/inspection */}
      <div className="absolute inset-0 opacity-25">
        <img
          src="https://images.unsplash.com/photo-1486006920555-c77dcf18193c?auto=format&fit=crop&w=2200&q=80"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-[#D4AF37]">Revisión 200 puntos</span>
            <h2 className="font-heading mt-6 text-3xl sm:text-5xl font-light tracking-tight text-[#FFFFF0] leading-[1.08]">
              Cada vehículo se inspecciona{" "}
              <span className="inline-block font-light gold-text px-[0.12em] mr-[0.08em] tracking-[-0.005em]">a fondo</span>
              antes de avanzar.
            </h2>
            <p className="mt-6 text-base text-[#A3A3A3] leading-relaxed font-light">
              Antes de cualquier negociación o transferencia económica, realizamos
              una revisión completa de 200 puntos: estado general, estética,
              mecánica y funcionalidad. Te enviamos un informe claro para que
              decidas con tranquilidad.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                "Revisión presencial en Alemania, no a distancia.",
                "Informe detallado con fotos y vídeo del vehículo.",
                "Verificación de historial, kilometraje real y documentación.",
                "Recomendación honesta: te decimos si NO conviene comprar.",
              ].map((point, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-3 text-sm sm:text-base text-[#FFFFF0] font-light"
                  data-testid={`inspection-point-${i}`}
                >
                  <CheckCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span>{point}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6"
          >
            {/* Big number card */}
            <div className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#141414] to-[#0A0A0A] p-8 sm:p-12 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(212,175,55,0.2) 0%, transparent 65%)" }}
              />

              <div className="relative">
                <div className="flex items-baseline gap-4 mb-2">
                  <motion.span
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="font-heading text-[7rem] sm:text-[10rem] font-extralight gold-text leading-none tracking-tighter"
                  >
                    200
                  </motion.span>
                  <span className="font-heading text-base sm:text-lg text-[#FFFFF0] mb-4">
                    puntos<br/>de control
                  </span>
                </div>
                <div className="gold-line my-6" />

                <div className="grid grid-cols-3 gap-4">
                  {stats.map((s, i) => (
                    <motion.div
                      key={s.label}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.3 + i * 0.1 }}
                    >
                      <p className="font-heading text-xl sm:text-2xl text-[#D4AF37] font-light">{s.value}</p>
                      <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#737373] mt-1 leading-tight">{s.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pillars */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/5 mt-4">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="bg-[#0A0A0A] p-5"
                  data-testid={`inspection-pillar-${i}`}
                >
                  <p.icon className="w-5 h-5 text-[#D4AF37] mb-3" strokeWidth={1.3} />
                  <p className="font-heading text-sm font-medium text-[#FFFFF0] mb-1.5">{p.title}</p>
                  <p className="text-xs text-[#A3A3A3] leading-relaxed font-light">{p.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Inspection;
